import './AlertDialog.scss';
import cn from 'classnames';
import { ReactNode, useMemo } from 'react';
import { Modal, ModalProps } from 'react-bootstrap';
import { Button, ButtonDesignType } from '../Button';
import { Appearance, AppearanceType, Design, Intent, IntentType } from '../designConstants';

export interface AlertDialogAction {
  onClick?: () => void;
  label?: string;
  appearance?: AppearanceType;
  design?: ButtonDesignType;
}

export type AlertDialogTertiaryAction = AlertDialogAction & { onClick: () => void; label: string };

interface AlertDialogProps {
  intent?: IntentType;
  escapable?: boolean;
  show: boolean;
  onHide: () => void;
  icon?: React.ReactNode;
  title: string;
  body: string | React.ReactNode;
  /** Defaults to "Okay", filled, with the design taken from `intent`. Closes the dialog after `onClick`. */
  primaryAction?: AlertDialogAction;
  /** Rendered only when `onClick` is set. Defaults to "Cancel", outlined neutral. Closes the dialog after `onClick`. */
  secondaryAction?: AlertDialogAction;
  /** Rendered on the left of the footer. Defaults to empty, with the design taken from `intent`. Closes the dialog after `onClick`. */
  tertiaryAction?: AlertDialogTertiaryAction;
  loading?: boolean;
  className?: string;
  additionalFooterContent?: React.ReactNode;
  showFooter?: boolean;
  centered?: boolean;
  dataTestId?: string;
  passthroughProps?: Partial<
    Omit<ModalProps, 'show' | 'onHide' | 'contentClassName' | 'centered' | 'backdrop' | 'keyboard'>
  >;
}

export const AlertDialog = ({
  intent = Intent.INFO,
  escapable = true,
  show,
  onHide,
  icon,
  title,
  body,
  primaryAction = {},
  secondaryAction = {},
  tertiaryAction,
  loading,
  className = '',
  additionalFooterContent,
  showFooter = true,
  centered = true,
  dataTestId,
  passthroughProps = {}
}: AlertDialogProps): ReactNode => {
  const intentDesign = intent === Intent.INFO ? Design.BRAND : intent;
  const {
    onClick: primaryOnClick,
    label: primaryLabel = 'Okay',
    appearance: primaryAppearance = Appearance.FILLED,
    design: primaryDesign = intentDesign
  } = primaryAction;
  const {
    onClick: secondaryOnClick,
    label: secondaryLabel = 'Cancel',
    appearance: secondaryAppearance = Appearance.OUTLINED,
    design: secondaryDesign = Design.NEUTRAL
  } = secondaryAction;
  const tertiaryOnClick = tertiaryAction?.onClick;

  // Action objects are new on every render, so these depend on the callbacks, not the objects.
  const [primaryOnClickWithHide, secondaryOnClickWithHide, tertiaryOnClickWithHide] =
    useMemo(() => {
      const withHide = (onClick?: () => void) => () => {
        onClick?.();
        onHide();
      };
      return [
        withHide(primaryOnClick),
        secondaryOnClick ? withHide(secondaryOnClick) : undefined,
        tertiaryOnClick ? withHide(tertiaryOnClick) : undefined
      ];
    }, [onHide, primaryOnClick, secondaryOnClick, tertiaryOnClick]);

  return (
    <Modal
      data-testid={dataTestId}
      {...passthroughProps}
      show={show}
      onHide={onHide}
      contentClassName={cn('alert-dialog', intent, className)}
      centered={centered}
      backdrop={escapable ? true : 'static'}
      keyboard={escapable}
    >
      <Modal.Body>
        {icon && <div>{icon}</div>}
        <div>
          <h4 className='dialog-title'>{title}</h4>
          <div className='dialog-body'>{body}</div>
        </div>
      </Modal.Body>
      {showFooter && (
        <Modal.Footer>
          {tertiaryAction && tertiaryOnClickWithHide && (
            <Button
              className='tertiary-action'
              disabled={loading}
              appearance={tertiaryAction.appearance ?? Appearance.EMPTY}
              design={tertiaryAction.design ?? intentDesign}
              onClick={tertiaryOnClickWithHide}
            >
              {tertiaryAction.label}
            </Button>
          )}
          {additionalFooterContent}
          {secondaryOnClickWithHide && (
            <Button
              disabled={loading}
              appearance={secondaryAppearance}
              design={secondaryDesign}
              onClick={secondaryOnClickWithHide}
            >
              {secondaryLabel}
            </Button>
          )}
          <Button
            loading={loading}
            appearance={primaryAppearance}
            design={primaryDesign}
            onClick={primaryOnClickWithHide}
          >
            {primaryLabel}
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
};
